// modules/BattleReward.js
import DropSystem from './DropSystem.js';
import { deepClone } from './utils/PlayerDataSync.js';
import GameStateStore from './GameStateStore.js';

export default class BattleReward {
    constructor(scene) {
        this.scene = scene;
        
        // 奖励配置
        this.config = {
            baseExpMultiplier: 1.0,
            baseMoneyMultiplier: 1.0,
            levelUpStats: {
                maxHp: 10,
                attack: 2,
                defense: 1,
                speed: 1
            }
        };
    }

    calculateRewards(defeatedEnemies) {
        if (!defeatedEnemies || defeatedEnemies.length === 0) {
            return this.createEmptyRewards();
        }
        
        let totalExp = 0;
        let totalMoney = 0;
        const items = [];
        
        // 计算每个被击败敌人的奖励
        defeatedEnemies.forEach(enemy => {
            // 经验值 - 直接从敌人数据获取
            if (enemy.exp) {
                totalExp += Math.floor(enemy.exp * this.config.baseExpMultiplier);
            }
            
            // 金钱 - 直接从敌人数据获取
            if (enemy.money) {
                totalMoney += Math.floor(enemy.money * this.config.baseMoneyMultiplier);
            }
            
            // 物品掉落 - 使用DropSystem计算（支持战车装备掉落）
            const difficulty = 1.0; // 可以根据副本难度调整
            const droppedItems = DropSystem.calculateDrops(enemy, difficulty, this.scene);
            if (droppedItems.length > 0) {
                // 转换掉落格式为奖励格式
                droppedItems.forEach(drop => {
                    // DropSystem.calculateDrops 已经确保返回完整数据，直接使用
                    const item = {
                        ...drop, // 保留所有属性（包括 name, type, description 等）
                        fromEnemy: enemy.name,
                        isRare: drop.chance < 10,
                        count: drop.count || 1
                    };
                    items.push(item);
                });
            }
            
        });
        
        // 额外奖励（连击、无伤等）
        const bonus = this.calculateBonusRewards(defeatedEnemies);
        totalExp += bonus.exp;
        totalMoney += bonus.money;
        
        return {
            exp: totalExp,
            money: totalMoney,
            items: items,
            bonus: bonus,
            levelUp: false,
            originalLevel: 0
        };
    }

    calculateItemDrops(enemy) {
        const items = [];
        
        // 检查敌人是否有掉落列表
        if (!enemy.drops || enemy.drops.length === 0) {
            return items;
        }
        
        // 遍历掉落列表
        enemy.drops.forEach(drop => {
            // 检查掉落几率
            if (drop.chance && Math.random() * 100 < drop.chance) {
                // 创建物品对象
                const item = {
                    id: drop.id,
                    name: drop.name,
                    type: drop.type,
                    fromEnemy: enemy.name,
                    isRare: drop.chance < 10, // 几率低于10%算稀有
                    usableInBattle: drop.usableInBattle || false,
                    price: drop.price || 0
                };
                
                // 根据物品类型添加特定属性
                switch(drop.type) {
                    case 'consumable':
                        if (drop.healAmount) item.healAmount = drop.healAmount;
                        if (drop.repairAmount) item.repairAmount = drop.repairAmount;
                        if (drop.description) item.description = drop.description;
                        break;
                    case 'weapon':
                        if (drop.damage) item.damage = drop.damage;
                        if (drop.special) item.special = drop.special;
                        break;
                    case 'armor':
                        if (drop.defense) item.defense = drop.defense;
                        break;
                    case 'material':
                        if (drop.value) item.value = drop.value;
                        break;
                }
                
                items.push(item);
            }
        });
        
        return items;
    }

    calculateBonusRewards(defeatedEnemies) {
        // 计算额外奖励
        let expBonus = 0;
        let moneyBonus = 0;
        let reason = '';
        
        // 基于击败的敌人数量给予额外奖励
        if (defeatedEnemies.length >= 3) {
            expBonus += Math.floor(defeatedEnemies.reduce((sum, enemy) => sum + (enemy.exp || 0), 0) * 0.2);
            moneyBonus += Math.floor(defeatedEnemies.reduce((sum, enemy) => sum + (enemy.money || 0), 0) * 0.2);
            reason += '击败多名敌人 ';
        }
        
        // 检查是否有BOSS（通过ID判断，避免硬编码名称）
        const hasBoss = defeatedEnemies.some(enemy => 
            enemy.id && enemy.id.includes('boss')
        );
        
        if (hasBoss) {
            expBonus += 20;
            moneyBonus += 50;
            reason += '击败BOSS ';
        }
        
        // 可以在这里添加更多额外奖励条件
        // 例如：无伤胜利、快速胜利、连击等
        
        return {
            exp: expBonus,
            money: moneyBonus,
            reason: reason || '战斗表现'
        };
    }

    createEmptyRewards() {
        return {
            exp: 0,
            money: 0,
            items: [],
            bonus: { exp: 0, money: 0, reason: '' },
            levelUp: false,
            originalLevel: 0
        };
    }

    applyRewards(player, rewards) {
        if (!player) return rewards;
        
        // 检查是否是组队模式
        const isTeamBattle = this.scene.isTeamBattle;
        const teamMembers = this.scene.teamMembers || [];
        
        if (isTeamBattle && teamMembers.length > 1) {
            // 组队模式：分配奖励给所有成员
            return this.applyTeamRewards(teamMembers, rewards);
        } else {
            // 单人模式：正常应用奖励
            return this.applySinglePlayerRewards(player, rewards);
        }
    }
    
    /**
     * 单人模式应用奖励
     */
    applySinglePlayerRewards(player, rewards) {
        // 保存原始等级
        rewards.originalLevel = player.level;
        
        // 应用经验值
        this.applyExperience(player, rewards.exp);
        
        // 应用金钱
        this.applyMoney(player, rewards.money);
        
        // 应用物品
        this.applyItems(player, rewards.items);
        
        // 检查是否升级
        if (player.level > rewards.originalLevel) {
            rewards.levelUp = true;
            rewards.newLevel = player.level;
            rewards.levelsGained = player.level - rewards.originalLevel;
            rewards.statIncreases = this.calculateStatIncreases(rewards.levelsGained);
        }
        
        return rewards;
    }
    
    /**
     * 组队模式应用奖励
     * 金钱和经验平均分配，掉落物随机分配
     */
    applyTeamRewards(teamMembers, rewards) {
        const memberCount = teamMembers.length;
        const networkManager = window.networkManager;
        const localPlayerId = networkManager ? networkManager.playerId : null;
        
        // 平均分配金钱和经验
        const expPerMember = Math.floor(rewards.exp / memberCount);
        const moneyPerMember = Math.floor(rewards.money / memberCount);
        
        // 记录分配详情（用于显示）
        rewards.teamDistribution = {
            expPerMember: expPerMember,
            moneyPerMember: moneyPerMember,
            itemDistribution: []
        };
        
        // 为每个成员分配金钱和经验
        teamMembers.forEach((member, index) => {
            if (member && member.playerData) {
                // 如果是本地玩家，直接应用到gameState.player
                const localPlayer = GameStateStore.getPlayer();
                if (member.playerId === localPlayerId && localPlayer) {
                    this.applyExperience(localPlayer, expPerMember);
                    this.applyMoney(localPlayer, moneyPerMember);
                    
                    // 记录本地玩家的奖励
                    rewards.originalLevel = localPlayer.level;
                    if (localPlayer.level > rewards.originalLevel) {
                        rewards.levelUp = true;
                        rewards.newLevel = localPlayer.level;
                        rewards.levelsGained = localPlayer.level - rewards.originalLevel;
                        rewards.statIncreases = this.calculateStatIncreases(rewards.levelsGained);
                    }
                } else {
                    // 其他队员：应用到他们的playerData
                    this.applyExperience(member.playerData, expPerMember);
                    this.applyMoney(member.playerData, moneyPerMember);
                }
            }
        });
        
        // 随机分配掉落物
        const items = [...rewards.items]; // 复制数组
        const shuffledItems = this.shuffleArray(items);
        
        // 随机分配给成员
        shuffledItems.forEach((item, index) => {
            const targetMemberIndex = index % memberCount;
            const targetMember = teamMembers[targetMemberIndex];
            
            if (targetMember && targetMember.playerData) {
                // 如果是本地玩家，直接应用到gameState.player
                const localPlayer = GameStateStore.getPlayer();
                if (targetMember.playerId === localPlayerId && localPlayer) {
                    this.applyItems(localPlayer, [item]);
                } else {
                    // 其他队员：应用到他们的playerData
                    if (!targetMember.playerData.inventory) {
                        targetMember.playerData.inventory = [];
                    }
                    this.applyItems(targetMember.playerData, [item]);
                }
                
                // 记录分配信息
                rewards.teamDistribution.itemDistribution.push({
                    item: item,
                    memberIndex: targetMemberIndex,
                    memberName: targetMember.playerName || '玩家'
                });
            }
        });
        
        return rewards;
    }
    
    /**
     * 随机打乱数组
     */
    shuffleArray(array) {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    applyExperience(player, exp) {
        if (!player || exp <= 0) return;
        
        const originalExp = player.exp || 0;
        player.exp = (player.exp || 0) + exp;
        
        
        // 如果还没有下一级经验，设置初始值
        if (!player.nextLevelExp) {
            player.nextLevelExp = this.calculateNextLevelExp(player.level || 1);
        }
        
        // 检查升级
        let levelsGained = 0;
        while (player.exp >= player.nextLevelExp) {
            levelsGained++;
            const overflowExp = player.exp - player.nextLevelExp;
            this.levelUpPlayer(player);
            player.exp = overflowExp;
        }
        
        if (levelsGained > 0) {
        }
    }

    calculateNextLevelExp(currentLevel) {
        // 简单经验曲线：每级增加50%
        return Math.floor(100 * Math.pow(1.5, currentLevel - 1));
    }

    levelUpPlayer(player) {
        player.level = (player.level || 1) + 1;
        
        // 增加属性
        player.maxHp = (player.maxHp || 100) + this.config.levelUpStats.maxHp;
        player.hp = player.maxHp; // 升级回满血
        player.attack = (player.attack || 0) + this.config.levelUpStats.attack;
        player.defense = (player.defense || 0) + this.config.levelUpStats.defense;
        
        // 计算下一级所需经验
        player.nextLevelExp = this.calculateNextLevelExp(player.level);
        
    }

    applyMoney(player, money) {
        if (!player || money <= 0) return;
        
        player.money = (player.money || 0) + money;
    }

    applyItems(player, items) {
        if (!player || !items || items.length === 0) return;
        
        if (!player.inventory) {
            player.inventory = [];
        }
        
        items.forEach(item => {
            const itemData = DropSystem.getItemById(item.id, this.scene);
            // 保留掉落相关属性，合并完整数据
            const preservedFields = {
                chance: item.chance,
                count: item.count,
                fromEnemy: item.fromEnemy,
                isRare: item.isRare,
                tier: item.tier
            };
            Object.assign(item, itemData, preservedFields);                
            
            // 处理特殊掉落：random_fragment - 从items.json中随机选择一个fragment类型的物品
            if (item.id === 'random_fragment') {
                const tier = item.tier || null; // 如果指定了tier，只从对应tier的碎片中选择
                const count = item.count || 1;
                
                // 从items.json中获取所有fragment类型的物品
                let itemsData = null;
                if (this.scene && this.scene.cache && this.scene.cache.json) {
                    itemsData = this.scene.cache.json.get('items');
                } else if (GameStateStore.get('cache')) {
                    itemsData = GameStateStore.get('cache').json?.get('items');
                }
                
                if (itemsData && itemsData.items) {
                    // 筛选出所有fragment类型的物品
                    let fragments = itemsData.items.filter(i => i.type === 'fragment' && i.id !== 'random_fragment');
                    
                    // 如果指定了tier，只选择对应tier的碎片
                    if (tier !== null) {
                        fragments = fragments.filter(f => f.tier === tier);
                    }
                    
                    if (fragments.length > 0) {
                        // 随机生成指定数量的碎片
                        for (let i = 0; i < count; i++) {
                            const randomIndex = Math.floor(Math.random() * fragments.length);
                            const randomFragment = deepClone(fragments[randomIndex]);
                            player.inventory.push(randomFragment);
                        }
                    }
                }
                return;
            }
            
            // 处理多个相同物品（count > 1）
            const count = item.count || 1;
            for (let i = 0; i < count; i++) {
                const itemCopy = { ...item };
                delete itemCopy.count; // 移除count，每个物品单独存储
                player.inventory.push(itemCopy);
            }
        });
    }

    calculateStatIncreases(levelsGained) {
        return {
            maxHp: levelsGained * this.config.levelUpStats.maxHp,
            attack: levelsGained * this.config.levelUpStats.attack,
            defense: levelsGained * this.config.levelUpStats.defense,
            speed: levelsGained * this.config.levelUpStats.speed
        };
    }

    showRewardMessage(rewards) {
        if (!rewards || (rewards.exp === 0 && rewards.money === 0 && rewards.items.length === 0)) {
            return '没有获得任何奖励';
        }
        
        // 检查是否是组队模式
        const isTeamBattle = this.scene.isTeamBattle;
        const teamMembers = this.scene.teamMembers || [];
        const networkManager = window.networkManager;
        const localPlayerId = networkManager ? networkManager.playerId : null;
        
        if (isTeamBattle && teamMembers.length > 1 && rewards.teamDistribution) {
            // 组队模式：显示分配详情
            return this.showTeamRewardMessage(rewards, teamMembers, localPlayerId);
        } else {
            // 单人模式：显示普通奖励消息
            return this.showSinglePlayerRewardMessage(rewards);
        }
    }
    
    /**
     * 显示单人模式奖励消息
     */
    showSinglePlayerRewardMessage(rewards) {
        let message = ``;
        
        // 计算基础奖励（总和减去额外奖励）
        const baseExp = rewards.bonus ? (rewards.exp - (rewards.bonus.exp || 0)) : rewards.exp;
        const baseMoney = rewards.bonus ? (rewards.money - (rewards.bonus.money || 0)) : rewards.money;
        
        // 基础奖励
        if (baseExp > 0) {
            message += `✨ 获得经验值: ${baseExp}\n`;
        }
        
        if (baseMoney > 0) {
            message += `💰 获得金钱: ${baseMoney}G\n`;
        }
        
        // 额外奖励
        if (rewards.bonus && (rewards.bonus.exp > 0 || rewards.bonus.money > 0)) {
            message += `\n🎁 额外奖励:\n`;
            if (rewards.bonus.exp > 0) {
                message += `   经验值 +${rewards.bonus.exp}\n`;
            }
            if (rewards.bonus.money > 0) {
                message += `   金钱 +${rewards.bonus.money}G\n`;
            }
            if (rewards.bonus.reason) {
                message += `   (${rewards.bonus.reason})\n`;
            }
        }
        
        // 物品
        if (rewards.items.length > 0) {
            message += `\n📦 获得物品:\n`;
            rewards.items.forEach(item => {
                // 跳过无效物品（没有name属性的）
                if (!item || !item.name) return;
                
                const rarity = item.isRare ? '⭐ ' : '   ';
                message += `${rarity}${item.name}`;
                if (item.type === 'consumable' && item.healAmount) {
                    message += ` (回复${item.healAmount}HP)`;
                }
                message += `\n`;
            });
        }
        
        // 升级信息
        if (rewards.levelUp) {
            message += `\n🎉 恭喜升级！\n`;
            message += `   等级: ${rewards.originalLevel} → ${rewards.newLevel}\n`;
            message += `   HP上限: +${rewards.statIncreases.maxHp}\n`;
            message += `   攻击力: +${rewards.statIncreases.attack}\n`;
            message += `   防御力: +${rewards.statIncreases.defense}\n`;
        }
        
        return message;
    }
    
    /**
     * 显示组队模式奖励消息
     */
    showTeamRewardMessage(rewards, teamMembers, localPlayerId) {
        let message = ``;
        const dist = rewards.teamDistribution;
        
        // 总奖励
        message += `👥 队伍奖励:\n`;
        message += `✨ 总经验值: ${rewards.exp}\n`;
        message += `💰 总金钱: ${rewards.money}G\n\n`;
        
        // 平均分配说明
        message += `📊 平均分配:\n`;
        message += `   每位成员获得:\n`;
        message += `   ✨ 经验值: ${dist.expPerMember}\n`;
        message += `   💰 金钱: ${dist.moneyPerMember}G\n\n`;
        
        // 物品分配（只显示本地玩家获得的）
        const localPlayerItems = dist.itemDistribution.filter(itemDist => {
            const member = teamMembers[itemDist.memberIndex];
            return member && member.playerId === localPlayerId;
        });
        
        if (localPlayerItems.length > 0) {
            message += `📦 你获得的物品:\n`;
            localPlayerItems.forEach(itemDist => {
                const item = itemDist.item;
                if (!item || !item.name) return;
                
                const rarity = item.isRare ? '⭐ ' : '   ';
                message += `${rarity}${item.name}`;
                if (item.type === 'consumable' && item.healAmount) {
                    message += ` (回复${item.healAmount}HP)`;
                }
                message += `\n`;
            });
        }
        
        // 其他成员获得的物品（可选显示）
        if (rewards.items.length > localPlayerItems.length) {
            message += `\n其他成员获得的物品: ${rewards.items.length - localPlayerItems.length} 件\n`;
        }
        
        // 额外奖励
        if (rewards.bonus && (rewards.bonus.exp > 0 || rewards.bonus.money > 0)) {
            message += `\n🎁 额外奖励 (已平均分配):\n`;
            if (rewards.bonus.exp > 0) {
                const bonusExpPerMember = Math.floor(rewards.bonus.exp / teamMembers.length);
                message += `   经验值 +${bonusExpPerMember}\n`;
            }
            if (rewards.bonus.money > 0) {
                const bonusMoneyPerMember = Math.floor(rewards.bonus.money / teamMembers.length);
                message += `   金钱 +${bonusMoneyPerMember}G\n`;
            }
            if (rewards.bonus.reason) {
                message += `   (${rewards.bonus.reason})\n`;
            }
        }
        
        // 升级信息
        if (rewards.levelUp) {
            message += `\n🎉 恭喜升级！\n`;
            message += `   等级: ${rewards.originalLevel} → ${rewards.newLevel}\n`;
            message += `   HP上限: +${rewards.statIncreases.maxHp}\n`;
            message += `   攻击力: +${rewards.statIncreases.attack}\n`;
            message += `   防御力: +${rewards.statIncreases.defense}\n`;
        }
        
        return message;
    }

    saveRewardsToGameState(rewards) {
        if (!GameStateStore.getState()) return;
        
        // 保存奖励信息到游戏状态（可选）
        if (!GameStateStore.get('battleHistory')) {
            GameStateStore.set('battleHistory', []);
        }
        
        const battleHistory = GameStateStore.get('battleHistory');
        battleHistory.push({
            timestamp: Date.now(),
            rewards: {
                exp: rewards.exp,
                money: rewards.money,
                items: rewards.items.filter(item => item && item.name).map(item => item.name),
                levelUp: rewards.levelUp
            }
        });
        
        // 限制历史记录数量
        if (battleHistory.length > 50) {
            battleHistory.shift();
        }
    }

    destroy() {
        // 清理资源
    }
}